import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, MicIcon, SquareIcon, XIcon, GripVerticalIcon, LoaderIcon, AlertCircleIcon, RefreshCwIcon, CheckCircle2Icon, SparklesIcon } from 'lucide-react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import OpenAI from 'openai';
import { TASK_ANALYSIS_PROMPT, STEP_VOICE_CORRECTION_PROMPT } from '../../config/aiPrompts';
import { useI18n } from '../../config/i18n';
// ── OpenAI helpers (same pattern as VoiceDescriptionScreen) ──────────────────
const STORAGE_KEY = 'ptp_openai_api_key';
function getSavedApiKey(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}
function getOpenAIClient(): OpenAI {
  const apiKey = getSavedApiKey();
  return new OpenAI({
    apiKey: apiKey || 'missing-key',
    dangerouslyAllowBrowser: true
  });
}
// ── Types ────────────────────────────────────────────────────────────────────
interface StepItem {
  id: string;
  text: string;
}
let stepIdCounter = 0;
function createStepItem(text: string): StepItem {
  return {
    id: `step-${++stepIdCounter}`,
    text
  };
}
// ── DraggableStep ────────────────────────────────────────────────────────────
function DraggableStep({
  item,
  index,
  editingIndex,
  editValue,
  setEditValue,
  onEditStart,
  onEditSave,
  onRemove,
  lang,
  data
}: {
  item: StepItem;
  index: number;
  editingIndex: number | null;
  editValue: string;
  setEditValue: (v: string) => void;
  onEditStart: (index: number, value: string) => void;
  onEditSave: (index: number) => void;
  onRemove: (index: number) => void;
  lang: string;
  data: any;
}) {
  const dragControls = useDragControls();
  return <Reorder.Item value={item} dragListener={false} dragControls={dragControls} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    delay: index * 0.05
  }} whileDrag={{
    scale: 1.02,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    zIndex: 50
  }} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2 list-none" data-id="element-1590">
      <button onPointerDown={e => dragControls.start(e)} className="p-1 text-gray-400 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none flex-shrink-0" data-id="element-1591">
        <GripVerticalIcon className="w-4 h-4" data-id="element-1592" />
      </button>

      <div className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-1593">
        {index + 1}
      </div>

      {editingIndex === index ? <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => onEditSave(index)} onKeyDown={e => e.key === 'Enter' && onEditSave(index)} className="flex-1 bg-gray-50 border border-yellow-400 rounded px-2 py-1 text-gray-900 text-sm outline-none" data-id="element-1594" /> : <p className="flex-1 text-gray-800 text-sm leading-snug" data-id="element-1595">
          {lang === 'es' && data.jobSteps_es?.[index] ? data.jobSteps_es[index] : item.text}
        </p>}

      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1596">
        <button onClick={() => onEditStart(index, item.text)} className="p-2 text-gray-400 hover:text-yellow-500 transition-colors" data-id="element-1597">
          <PencilIcon className="w-4 h-4" data-id="element-1598" />
        </button>
        <button onClick={() => onRemove(index)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-id="element-1599">
          <TrashIcon className="w-4 h-4" data-id="element-1600" />
        </button>
      </div>
    </Reorder.Item>;
}
// ── Main Component ───────────────────────────────────────────────────────────
export function TaskUnderstandingScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    lang,
    t
  } = useI18n();
  // ── Step state ─────────────────────────────────────────────────────────────
  const [stepItems, setStepItems] = useState<StepItem[]>(() => data.jobSteps.map(createStepItem));
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  // ── AI analysis state ──────────────────────────────────────────────────────
  // Start in loading state if we have a description to analyze and no cached steps
  const [isAnalyzing, setIsAnalyzing] = useState(() => {
    return !!data.taskDescription && data.jobSteps.length === 0;
  });
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [detectedTaskTitle, setDetectedTaskTitle] = useState<string>(data.taskTitle || (data.taskDescription ? '' : 'Task Steps'));
  const hasRunAI = useRef(false);
  // ── Voice correction state ─────────────────────────────────────────────────
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isApplyingCorrections, setIsApplyingCorrections] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState('');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [correctionApplied, setCorrectionApplied] = useState(false);
  const [correctionSummary, setCorrectionSummary] = useState('');
  // ── MediaRecorder refs ─────────────────────────────────────────────────────
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  // ── Sync steps to parent ───────────────────────────────────────────────────
  const syncToParent = useCallback((items: StepItem[]) => {
    updateData({
      jobSteps: items.map(s => s.text)
    });
  }, [updateData]);
  // ── On-mount AI step generation ────────────────────────────────────────────
  useEffect(() => {
    if (hasRunAI.current) return;
    hasRunAI.current = true;
    // If we already have steps (user navigated back), don't re-analyze
    if (data.jobSteps.length > 0) {
      setIsAnalyzing(false);
      setDetectedTaskTitle(data.taskTitle || data.taskDescription || 'Task Steps');
      return;
    }
    const apiKey = getSavedApiKey();
    if (!apiKey || !data.taskDescription) {
      // No API key or no description — generate fallback placeholder steps
      setIsAnalyzing(false);
      const title = data.taskDescription || 'Construction Task';
      setDetectedTaskTitle(title);
      const fallbackSteps = ['Review scope of work and verify site conditions', 'Set up work area and stage materials and equipment', 'Execute primary task per method of procedure', 'Perform final inspection and secure work area'];
      const fallbackCrew = [{
        name: 'Mike Torres',
        role: 'Foreman',
        role_es: 'Capataz',
        responsibility: 'Oversee task execution and safety compliance',
        responsibility_es: 'Supervisar ejecución y cumplimiento de seguridad'
      }, {
        name: 'Dan Kowalski',
        role: 'Lead Worker',
        role_es: 'Trabajador Principal',
        responsibility: 'Execute primary task operations',
        responsibility_es: 'Ejecutar operaciones principales'
      }, {
        name: 'Luis Reyes',
        role: 'Laborer',
        role_es: 'Obrero',
        responsibility: 'Assist with material handling and site prep',
        responsibility_es: 'Asistir con manejo de materiales y preparación'
      }];
      const newItems = fallbackSteps.map(createStepItem);
      setStepItems(newItems);
      syncToParent(newItems);
      const fallbackEquipment = ['Tape measure', 'Hand tools (hammer, wrench set)', 'Power drill', 'Level', 'Safety barriers / cones'];
      updateData({
        taskTitle: title,
        taskTitle_es: title,
        jobSteps_es: ['Revisar alcance del trabajo y verificar condiciones del sitio', 'Preparar área de trabajo y organizar materiales y equipos', 'Ejecutar tarea principal según procedimiento', 'Realizar inspección final y asegurar área de trabajo'],
        crewRoles: fallbackCrew,
        crewSize: fallbackCrew.length,
        taskEquipment: fallbackEquipment,
        taskEquipment_es: ['Cinta métrica', 'Herramientas manuales (martillo, juego de llaves)', 'Taladro eléctrico', 'Nivel', 'Barreras de seguridad / conos']
      });
      return;
    }
    async function analyzeTask() {
      setIsAnalyzing(true);
      setAnalyzeError(null);
      try {
        const client = getOpenAIClient();
        const completion = await client.chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: TASK_ANALYSIS_PROMPT
          }, {
            role: 'user',
            content: data.taskDescription
          }],
          temperature: 0.3,
          max_tokens: 800
        });
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        // Parse JSON — handle markdown code fences
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) {
          jsonStr = fenceMatch[1].trim();
        }
        const parsed = JSON.parse(jsonStr) as {
          taskTitle: string;
          taskTitle_es?: string;
          steps: string[];
          steps_es?: string[];
          crew?: {
            name: string;
            role: string;
            role_es?: string;
            responsibility: string;
            responsibility_es?: string;
          }[];
          equipment?: string[];
          equipment_es?: string[];
        };
        if (!parsed.taskTitle || !Array.isArray(parsed.steps) || parsed.steps.length === 0) {
          throw new Error('Invalid response structure');
        }
        setDetectedTaskTitle(parsed.taskTitle);
        const newItems = parsed.steps.map(createStepItem);
        setStepItems(newItems);
        syncToParent(newItems);
        // Save task title, equipment to data
        updateData({
          taskTitle: parsed.taskTitle,
          taskTitle_es: parsed.taskTitle_es,
          jobSteps_es: parsed.steps_es,
          taskEquipment: parsed.equipment || [],
          taskEquipment_es: parsed.equipment_es || []
        });
        // Update crew if AI generated it
        if (Array.isArray(parsed.crew) && parsed.crew.length > 0) {
          updateData({
            crewRoles: parsed.crew.map(c => ({
              name: c.name,
              role: c.role,
              role_es: c.role_es,
              responsibility: c.responsibility,
              responsibility_es: c.responsibility_es
            })),
            crewSize: parsed.crew.length
          });
        }
      } catch (err: any) {
        console.error('[AI Analysis] Failed:', err);
        const msg = err?.message || String(err);
        if (err?.status === 401) {
          setAnalyzeError('Invalid OpenAI API key. Steps shown are defaults — you can edit them manually.');
        } else {
          setAnalyzeError(`AI analysis failed: ${msg}. You can edit steps manually.`);
        }
        // Keep existing steps as fallback
        setDetectedTaskTitle(data.taskDescription || 'Task Steps');
      } finally {
        setIsAnalyzing(false);
      }
    }
    analyzeTask();
  }, [data.taskDescription, syncToParent]);
  // ── Cleanup streams on unmount ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);
  // ── Step manipulation handlers ─────────────────────────────────────────────
  const handleReorder = (newItems: StepItem[]) => {
    setStepItems(newItems);
    syncToParent(newItems);
  };
  const handleRemove = (index: number) => {
    const newItems = stepItems.filter((_, i) => i !== index);
    setStepItems(newItems);
    syncToParent(newItems);
  };
  const handleEditStart = (index: number, value: string) => {
    setEditingIndex(index);
    setEditValue(value);
  };
  const handleEditSave = (index: number) => {
    const newItems = [...stepItems];
    newItems[index] = {
      ...newItems[index],
      text: editValue
    };
    setStepItems(newItems);
    syncToParent(newItems);
    setEditingIndex(null);
  };
  const handleAddStep = () => {
    const newItem = createStepItem('New step');
    const newItems = [...stepItems, newItem];
    setStepItems(newItems);
    syncToParent(newItems);
    setEditingIndex(newItems.length - 1);
    setEditValue('New step');
  };
  // ── Retry AI analysis ──────────────────────────────────────────────────────
  const handleRetryAnalysis = () => {
    hasRunAI.current = false;
    setAnalyzeError(null);
    setIsAnalyzing(false);
    // Trigger re-run by resetting the ref and forcing a re-render
    hasRunAI.current = false;
    (async () => {
      setIsAnalyzing(true);
      setAnalyzeError(null);
      try {
        const client = getOpenAIClient();
        const completion = await client.chat.completions.create({
          model: 'gpt-4o-mini',
          messages: [{
            role: 'system',
            content: TASK_ANALYSIS_PROMPT
          }, {
            role: 'user',
            content: data.taskDescription
          }],
          temperature: 0.3,
          max_tokens: 700
        });
        const raw = completion.choices[0]?.message?.content?.trim() || '';
        let jsonStr = raw;
        const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (fenceMatch) jsonStr = fenceMatch[1].trim();
        const parsed = JSON.parse(jsonStr) as {
          taskTitle: string;
          steps: string[];
          crew?: {
            name: string;
            role: string;
            responsibility: string;
          }[];
        };
        if (!parsed.taskTitle || !Array.isArray(parsed.steps) || parsed.steps.length === 0) {
          throw new Error('Invalid response structure');
        }
        setDetectedTaskTitle(parsed.taskTitle);
        const newItems = parsed.steps.map(createStepItem);
        setStepItems(newItems);
        syncToParent(newItems);
        // Save task title to data
        updateData({
          taskTitle: parsed.taskTitle
        });
        // Update crew if AI generated it
        if (Array.isArray(parsed.crew) && parsed.crew.length > 0) {
          updateData({
            crewRoles: parsed.crew.map(c => ({
              name: c.name,
              role: c.role,
              responsibility: c.responsibility
            })),
            crewSize: parsed.crew.length
          });
        }
      } catch (err: any) {
        setAnalyzeError(`Retry failed: ${err?.message || String(err)}`);
      } finally {
        setIsAnalyzing(false);
      }
    })();
  };
  // ── Voice recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setVoiceTranscription('');
      setHasRecorded(false);
      setCorrectionApplied(false);
      setCorrectionSummary('');
    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceError('Microphone access denied. Please allow permission and try again.');
    }
  }, []);
  const stopRecording = useCallback(async () => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      setVoiceError('Recording failed. Please try again.');
      return;
    }
    setIsRecording(false);
    setIsTranscribing(true);
    setVoiceError(null);
    const audioBlob = await new Promise<Blob>(resolve => {
      mediaRecorder.onstop = () => {
        resolve(new Blob(chunksRef.current, {
          type: 'audio/webm'
        }));
      };
      mediaRecorder.stop();
    });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioBlob.size < 1000) {
      setIsTranscribing(false);
      setVoiceError('Recording too short. Speak for at least 2 seconds.');
      return;
    }
    try {
      const file = new File([audioBlob], 'correction.webm', {
        type: 'audio/webm'
      });
      const client = getOpenAIClient();
      const response = await client.audio.transcriptions.create({
        model: 'whisper-1',
        file,
        language: lang === 'es' ? 'es' : 'en'
      });
      const rawText = response.text.trim();
      if (!rawText) {
        setIsTranscribing(false);
        setVoiceError('No speech detected. Please speak clearly and try again.');
        return;
      }
      setVoiceTranscription(rawText);
      setHasRecorded(true);
      setIsTranscribing(false);
    } catch (err: any) {
      console.error('[Whisper] Transcription failed:', err);
      setIsTranscribing(false);
      const statusCode = err?.status || err?.statusCode;
      if (statusCode === 401) {
        setVoiceError('Invalid OpenAI API key.');
      } else if (statusCode === 429) {
        setVoiceError('Rate limit reached. Please wait and try again.');
      } else {
        setVoiceError(`Transcription failed: ${err?.message || String(err)}`);
      }
    }
  }, []);
  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };
  // ── Apply voice corrections via GPT ────────────────────────────────────────
  const handleApplyCorrections = async () => {
    if (!voiceTranscription) return;
    setIsApplyingCorrections(true);
    setVoiceError(null);
    const currentStepsText = stepItems.map((s, i) => `${i + 1}. ${s.text}`).join('\n');
    try {
      const client = getOpenAIClient();
      const completion = await client.chat.completions.create({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system',
          content: STEP_VOICE_CORRECTION_PROMPT
        }, {
          role: 'user',
          content: `Current steps:\n${currentStepsText}\n\nUser corrections: "${voiceTranscription}"`
        }],
        temperature: 0.3,
        max_tokens: 500
      });
      const raw = completion.choices[0]?.message?.content?.trim() || '';
      let jsonStr = raw;
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (fenceMatch) jsonStr = fenceMatch[1].trim();
      const parsed = JSON.parse(jsonStr) as {
        steps: string[];
      };
      if (!Array.isArray(parsed.steps) || parsed.steps.length === 0) {
        throw new Error('Invalid response structure');
      }
      const newItems = parsed.steps.map(createStepItem);
      setStepItems(newItems);
      syncToParent(newItems);
      setCorrectionApplied(true);
      setCorrectionSummary(`Updated to ${parsed.steps.length} steps`);
      // Auto-close after brief success display
      setTimeout(() => {
        setShowVoiceModal(false);
        // Reset modal state after close animation
        setTimeout(() => {
          setHasRecorded(false);
          setVoiceTranscription('');
          setCorrectionApplied(false);
          setCorrectionSummary('');
        }, 300);
      }, 1200);
    } catch (err: any) {
      console.error('[GPT Correction] Failed:', err);
      setVoiceError(`Failed to apply corrections: ${err?.message || String(err)}`);
    } finally {
      setIsApplyingCorrections(false);
    }
  };
  // ── Loading state ──────────────────────────────────────────────────────────
  if (isAnalyzing) {
    return <div className="flex flex-col h-full items-center justify-center" data-id="element-1601">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        ease: 'linear'
      }} className="mb-5" data-id="element-1602">
          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-1603" />
        </motion.div>
        <p className="text-gray-900 font-semibold text-base mb-1" data-id="element-1604">
          Analyzing task description…
        </p>
        <p className="text-gray-400 text-xs text-center max-w-[220px]" data-id="element-1605">
          {t('task.loadingSteps')}
        </p>
      </div>;
  }
  // ── Main render ────────────────────────────────────────────────────────────
  return <div className="flex flex-col h-full relative" data-id="element-1606">
      <div className="mb-3" data-id="element-1607">
        <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1608">
          Detected Work Scope
        </h2>

        <div className="bg-white border border-gray-200 rounded-xl p-3 border-l-4 border-l-yellow-500" data-id="element-1609">
          <div className="flex items-center gap-2" data-id="element-1610">
            <SparklesIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" data-id="element-1611" />
            <h3 className="text-gray-900 font-bold text-base leading-snug" data-id="element-1612">
              {lang === 'es' && data.taskTitle_es ? data.taskTitle_es : detectedTaskTitle || 'Task Steps'}
            </h3>
          </div>
          {data.taskDescription && <p className="text-gray-400 text-xs mt-1.5 line-clamp-2" data-id="element-1613">
              {data.taskDescription}
            </p>}
        </div>
      </div>

      {/* Error banner */}
      <AnimatePresence data-id="element-1614">
        {analyzeError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden mb-3" data-id="element-1615">
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-1616">
              <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1617" />
              <div className="flex-1 min-w-0" data-id="element-1618">
                <p className="text-amber-700 text-xs leading-relaxed" data-id="element-1619">
                  {analyzeError}
                </p>
              </div>
              <button onClick={handleRetryAnalysis} className="flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-600 text-[10px] font-bold uppercase tracking-wider flex-shrink-0 hover:bg-amber-200 transition-colors" data-id="element-1620">
                <RefreshCwIcon className="w-3 h-3" data-id="element-1621" />
                Retry
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex-1 overflow-y-auto pb-20 -mx-4 px-4" data-id="element-1622">
        <div className="flex items-center justify-between mb-4 mt-2" data-id="element-1623">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1624">
            {stepItems.length > 0 ? `${t('task.mopSteps')} (${stepItems.length})` : t('task.mopSteps')}
          </p>
          <button onClick={() => {
          setShowVoiceModal(true);
          setVoiceError(null);
          setCorrectionApplied(false);
          setCorrectionSummary('');
        }} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-id="element-1625">
            <MicIcon className="w-3.5 h-3.5 text-yellow-500" data-id="element-1626" />
            Correct with Voice
          </button>
        </div>

        {stepItems.length === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center" data-id="element-1627">
            <p className="text-gray-400 text-sm font-medium mb-1" data-id="element-1628">
              No steps yet
            </p>
            <p className="text-gray-400 text-xs" data-id="element-1629">
              Add steps manually or use voice to describe them
            </p>
          </div> : <Reorder.Group axis="y" values={stepItems} onReorder={handleReorder} className="space-y-3" as="div" data-id="element-1630">
            <AnimatePresence data-id="element-1631">
              {stepItems.map((item, index) => <DraggableStep key={item.id} item={item} index={index} editingIndex={editingIndex} editValue={editValue} setEditValue={setEditValue} onEditStart={handleEditStart} onEditSave={handleEditSave} onRemove={handleRemove} lang={lang} data={data} data-id="element-1632" />)}
            </AnimatePresence>
          </Reorder.Group>}

        <motion.button whileTap={{
        scale: 0.98
      }} onClick={handleAddStep} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 font-medium text-sm flex items-center justify-center gap-2 hover:border-gray-400 hover:text-gray-700 transition-colors" data-id="element-1633">
          <PlusIcon className="w-4 h-4" data-id="element-1634" />
          {t('task.addStep')}
        </motion.button>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 bg-gray-50" data-id="element-1635">
        <PrimaryButton onClick={onNext} disabled={stepItems.length === 0} data-id="element-1636">
          {t('continue')}
        </PrimaryButton>
      </div>

      {/* ── Voice Correction Modal ──────────────────────────────────────────── */}
      <AnimatePresence data-id="element-1637">
        {showVoiceModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isRecording && !isTranscribing && !isApplyingCorrections && setShowVoiceModal(false)} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-1638" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 rounded-t-2xl z-50 px-5 pt-4 pb-6 max-h-[75%] flex flex-col overflow-hidden" data-id="element-1639">
              {/* Header */}
              <div className="flex items-center justify-between mb-2" data-id="element-1640">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1641">
                  Voice Correction
                </h3>
                <button onClick={() => setShowVoiceModal(false)} disabled={isRecording || isTranscribing || isApplyingCorrections} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 disabled:opacity-50" data-id="element-1642">
                  <XIcon className="w-5 h-5" data-id="element-1643" />
                </button>
              </div>

              <p className="text-gray-500 text-sm mb-4" data-id="element-1644">
                {t('task.voiceCorrection')} edit.
              </p>

              {/* Success state */}
              {correctionApplied ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex-1 flex flex-col items-center justify-center space-y-3" data-id="element-1645">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15
            }} data-id="element-1646">
                    <CheckCircle2Icon className="w-12 h-12 text-green-600" data-id="element-1647" />
                  </motion.div>
                  <p className="text-gray-900 font-semibold text-sm" data-id="element-1648">
                    Steps updated!
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-1649">{correctionSummary}</p>
                </motion.div> : <>
                  {/* Recording / Transcribing / Applying UI */}
                  <div className="flex-1 flex flex-col items-center justify-center space-y-4 mb-4 min-h-0" data-id="element-1650">
                    {/* Applying corrections spinner */}
                    {isApplyingCorrections ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-1651">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1.2,
                  ease: 'linear'
                }} data-id="element-1652">
                          <LoaderIcon className="w-10 h-10 text-yellow-500" data-id="element-1653" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-1654">
                          Applying corrections…
                        </p>
                        <p className="text-gray-400 text-xs text-center" data-id="element-1655">
                          AI is merging your changes with the existing steps
                        </p>
                      </motion.div> : isTranscribing ? <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex flex-col items-center space-y-3" data-id="element-1656">
                        <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} data-id="element-1657">
                          <LoaderIcon className="w-10 h-10 text-blue-600" data-id="element-1658" />
                        </motion.div>
                        <p className="text-gray-900 font-medium text-sm" data-id="element-1659">
                          Transcribing…
                        </p>
                      </motion.div> : <>
                        {/* Mic button */}
                        <div className="relative flex items-center justify-center" data-id="element-1660">
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.3, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5
                  }} className="absolute w-24 h-24 bg-yellow-100 rounded-full" data-id="element-1661" />}
                          {isRecording && <motion.div animate={{
                    scale: [1, 1.15, 1]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: 0.2
                  }} className="absolute w-20 h-20 bg-yellow-200 rounded-full" data-id="element-1662" />}
                          <motion.button whileTap={{
                    scale: 0.9
                  }} onClick={handleRecordToggle} className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-400/30' : 'bg-yellow-400 shadow-yellow-400/30'}`} data-id="element-1663">
                            {isRecording ? <SquareIcon className="w-6 h-6 text-white fill-current" data-id="element-1664" /> : <MicIcon className="w-6 h-6 text-gray-900" data-id="element-1665" />}
                          </motion.button>
                        </div>

                        <div className="text-center h-6" data-id="element-1666">
                          <p className={`font-medium text-sm ${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500'}`} data-id="element-1667">
                            {isRecording ? 'Recording…' : hasRecorded ? 'Tap to re-record' : 'Tap to start'}
                          </p>
                        </div>
                      </>}

                    {/* Voice error */}
                    {voiceError && <p className="text-red-500 text-xs text-center max-w-[250px]" data-id="element-1668">
                        {voiceError}
                      </p>}

                    {/* Transcription result */}
                    {hasRecorded && voiceTranscription && !isApplyingCorrections && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} transition={{
                duration: 0.3
              }} className="w-full p-3 rounded-xl bg-gray-50 border border-gray-200 overflow-y-auto max-h-[20vh]" data-id="element-1669">
                          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1" data-id="element-1670">
                            Transcription
                          </p>
                          <p className="text-gray-700 text-sm leading-snug" data-id="element-1671">
                            "{voiceTranscription}"
                          </p>
                        </motion.div>}
                  </div>

                  {/* Action button */}
                  {!isTranscribing && !isApplyingCorrections && <PrimaryButton onClick={handleApplyCorrections} disabled={!hasRecorded || isRecording || !voiceTranscription} data-id="element-1672">
                      Apply Corrections
                    </PrimaryButton>}
                </>}
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}